/**
 * Refusals for tools whose backing store only exists on the hosted Worker.
 *
 * stdio stubs every Cloudflare binding to null. Reading a method off null throws
 * a SYNCHRONOUS TypeError, so a chained `.catch(() => null)` does not rescue it,
 * and the user gets `Cannot read properties of null (reading 'get')` — which
 * reads as a broken tool rather than an unconfigured one. Six tools shipped that
 * way.
 *
 * The alternative that must NOT be taken is returning zeroes: a credit balance
 * of 0 or an empty event list is indistinguishable from a real answer, and it is
 * wrong. So these throw, the tool wrapper turns the throw into `isError: true`,
 * and the message names the hosted surface that does have the data.
 *
 * The remedy has to be one the reader can carry out. "Add the binding to
 * wrangler.jsonc and redeploy" and "add it to Doppler carrier/dev+stg+prd" are
 * instructions for whoever operates the Worker; someone running the published
 * CLI has neither file nor project.
 */

import type { Env } from "./types.js";

/** The hosted MCP endpoint, which holds every binding this package lacks. */
export const HOSTED_MCP_URL = "https://mcp.carrier.llc/mcp";

/**
 * Thrown when a tool's data lives in a binding this process does not have.
 *
 * A named type so a caller can tell a missing binding from a failed call; the
 * two need different words, and conflating them is how "not configured" reaches
 * a user as "broken".
 */
export class BindingUnavailableError extends Error {
  constructor(
    readonly tool: string,
    readonly binding: string,
    message: string,
  ) {
    super(message);
    this.name = "BindingUnavailableError";
  }
}

/**
 * Refuse a tool that cannot answer without `CARRIER_USERS`.
 *
 * `subject` says what the tool was going to read, in the user's terms — "your
 * MCP plan credit ledger", not the KV key. Nobody debugging this knows the key.
 */
export function requireUserStore(
  env: Env,
  tool: string,
  subject: string,
): void {
  requireBinding(env.CARRIER_USERS, tool, "CARRIER_USERS", subject);
}

/**
 * Refuse a tool whose named binding is absent.
 *
 * The general form of the above: `OAUTH_KV` and `OCS_EVENT_ROUTING` are stubbed
 * to null in exactly the same way and crashed in exactly the same way.
 */
export function requireBinding(
  binding: unknown,
  tool: string,
  name: string,
  subject: string,
): void {
  if (binding) return;
  throw new BindingUnavailableError(
    tool,
    name,
    `${tool} cannot answer here: ${subject} is held by the hosted Carrier service, ` +
      `and this local MCP server has no connection to it. There is nothing to set in ` +
      `your environment — the data does not exist on this machine. Connect to the ` +
      `hosted MCP at ${HOSTED_MCP_URL}, or open the console at https://app.carrier.llc, ` +
      `to use this tool. Every OCS tool in this server works against your own token ` +
      `and is unaffected.`,
  );
}
